import { useCallback, useSyncExternalStore } from "react";

import { usePendingApprovals } from "@/features/approvals/use-approvals";
import { EMPTY_FILTERS } from "@/features/my-applications/filters-url";
import { useMyApplications } from "@/features/my-applications/use-my-applications";

import {
  deriveNotifications,
  type NotificationItem,
} from "./derive-notifications";
import {
  getReadState,
  markAllRead as markAllReadInStore,
  markRead as markReadInStore,
  subscribeReadState,
} from "./read-state-store";

export type NotificationsState = {
  readonly items: readonly NotificationItem[];
  readonly unreadCount: number;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly markAllRead: () => void;
  /** 1 件だけ既読にする（通知を開いたとき）。 */
  readonly markRead: (_item: NotificationItem) => void;
};

const SOURCE_PAGE_SIZE = 50;

/**
 * 通知の状態。専用 API を持たず、承認待ちタスクと自分の案件から導出する
 * （issue #29 の確定仕様・MVP）。
 */
export function useNotifications(): NotificationsState {
  const approvals = usePendingApprovals();
  const cases = useMyApplications({
    filters: EMPTY_FILTERS,
    page: 1,
    perPage: SOURCE_PAGE_SIZE,
  });

  // 既読は端末ごとの簡易実装。ヘッダのベルと通知一覧が別々にこのフックを呼ぶので、
  // React の state ではなく外部ストアを購読して両方に反映する。
  const readState = useSyncExternalStore(subscribeReadState, getReadState);

  const markAllRead = useCallback(() => {
    markAllReadInStore();
  }, []);

  const markRead = useCallback((item: NotificationItem) => {
    // 保存するのは「その通知が指す出来事の発生時刻」。同じ id が使い回されるため、
    // 発生時刻がこれより新しくなったら（再差し戻し等）また未読に戻す
    markReadInStore(item.id, item.occurredAt?.getTime() ?? Date.now());
  }, []);

  const items = deriveNotifications({
    approvals: approvals.data ?? [],
    myCases: cases.data?.items ?? [],
    lastSeenAt: readState.lastSeenAt,
    readAt: readState.readAt,
  });

  return {
    items,
    unreadCount: items.filter((item) => item.unread).length,
    isPending: approvals.isPending || cases.isPending,
    isError: approvals.isError || cases.isError,
    markAllRead,
    markRead,
  };
}
